import { Button } from "primereact/button";
import { useCallback, useContext, useEffect, useState } from "react";
import { set, useFieldArray, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate, useOutletContext } from "react-router";
import PrimeDialog from "../../../components/dialog/PrimeDialog";
import PanelDataTable from "../../../components/PanelDataTable";
import { useEditableFlag } from "../../../hooks/useEditableFlag";
import { SystemLoading, checkLoading } from "../../../store/loadingStore";
import { MessageContext, handleSuccessMessage } from "../../../store/messageStore";
import {
  F106030702Before,
  defaultEmissionSourceLoad,
  queryCbamEmissionSource,
  updateCbamEmissionSource,
} from "../../../utility/service/cbam-service";
import { getCBAMSubItemsByItemNo } from "../../../utility/service/companyOffering-service";
import { Base64Encoder, BcryptEncoder, checkDpSuccess } from "../../../utility/service/tool-service";
import { addFormValidator } from "../../../utility/validate/formValidator";

const EmissionSourceInfo = ({ setActiveIndex }) => {
  const { t } = useTranslation();
  const [, loading] = useContext(SystemLoading);
  const [, dispatch] = useContext(MessageContext); //訊息視窗
  const {
    action,
    setAction,
    currentCompany,
    setCurrentCompany,
    currentCbamMasterSeq,
    setCurrentCbamMasterSeq,
  } = useOutletContext();
  //表單驗證
  const [formRule, setFormRule] = useState([]);
  // 下拉選單
  const [adUnitList, setAdUnitList] = useState([]);
  const [efUnitList, setEfUnitList] = useState([]);
  const [emissionTypeList, setEmissionTypeList] = useState([]);
  const [methodList, setMethodList] = useState([]);
  const [gasTypeList, setGasTypeList] = useState([]);
  // 顯示單位
  const [ncvUnit, setNcvUnit] = useState({});
  const [carbonUnit, setCarbonUnit] = useState({});
  // 設備帶入popup
  const [confirmPopup, setConfirmPopup] = useState(false);
  const isEditable = useEditableFlag("F10603");
  const [tabAction, setTabAction] = useState("detail");
  const editFlag = tabAction === "detail" || !isEditable;
  const [oriData, setOriData] = useState({});

  const {
    getValues,
    setValue,
    watch,
    control,
    register,
    handleSubmit,
    clearErrors,
    trigger,
    reset,
    formState: { errors, isValid, isDirty },
  } = useForm({
    defaultValues: {},
    //錯誤驗證時機
    mode: "all",
  });

  const NCVMap = {
    t: "GJ/t",
    "1000Nm3": "GJ/1000Nm3",
  };
  const carbonMap = {
    t: "tC/t",
    "1000Nm3": "tC/1000Nm3",
  };

  useEffect(() => {
    axios_getCBAMSubItemsByItemNo("CBAM_EMISSION_TYPE", 4).then(setEmissionTypeList);
    axios_getCBAMSubItemsByItemNo("EF_UNIT", 12).then(setEfUnitList);
    axios_getCBAMSubItemsByItemNo("AD_UNIT", 13).then(setAdUnitList);
    axios_getCBAMSubItemsByItemNo("PFC_METHOD", 14).then(setMethodList);
    axios_getCBAMSubItemsByItemNo("GHG_TYPE", 15).then(setGasTypeList);
    axios_queryCbamEmissionSource({ companySeq: currentCompany?.companySeq, cbamMasterSeq: currentCbamMasterSeq });
    axios_F106030702Before();
  }, []);

  const axios_getCBAMSubItemsByItemNo = (itemNo, orderNo) => {
    return getCBAMSubItemsByItemNo(itemNo, orderNo).then((res) => {
      if (checkDpSuccess(res)) {
        let data = res.data.RespBody.subItems;
        return data.map((item, index) => ({
          ...item,
          index,
        }));
      }
      return [];
    });
  };

  const axios_queryCbamEmissionSource = (reqBody) => {
    checkLoading(loading);
    queryCbamEmissionSource(reqBody)
      .then((res) => {
        if (checkDpSuccess(res)) {
          let data = res?.data.RespBody;
          const oriData = {};
          if (data?.cbamEminstNopfcList?.length <= 0 && noPfcFields.length === 0) {
            addNoPfcForm(defaultNoPfcFields);
          } else {
            oriData.cbamEminstNopfcList = data.cbamEminstNopfcList;
            setNcvUnit(
              (data.cbamEminstNopfcList || []).reduce((acc, item, index) => {
                acc[index] = NCVMap[item.consumptionUnit] || null;
                return acc;
              }, {}),
            );
            setCarbonUnit(
              (data.cbamEminstNopfcList || []).reduce((acc, item, index) => {
                acc[index] = carbonMap[item.consumptionUnit] || null;
                return acc;
              }, {}),
            );
          }
          if (data?.cbamEminstPfcList?.length > 0) {
            oriData.cbamEminstPfcList = data.cbamEminstPfcList;
          }
          if (data?.cbamEminstMeasList?.length > 0) {
            oriData.cbamEminstMeasList = data.cbamEminstMeasList;
          }
          setOriData(oriData);
          Object.entries(oriData).forEach(([key, value]) => {
            setValue(key, value);
          });
        }
      })
      .finally((fin) => {
        checkLoading(loading, false);
      });
  };

  const axios_defaultEmissionSourceLoad = (reqBody) => {
    checkLoading(loading);
    defaultEmissionSourceLoad(reqBody)
      .then((res) => {
        if (checkDpSuccess(res)) {
          let data = res.data.RespBody;
          delNoPfcForm(noPfcFields.map((_, index) => index));
          setValue("cbamEminstNopfcList", data.cbamEminstNopfcList);
          setNcvUnit(
            (data.cbamEminstNopfcList || []).reduce((acc, item, index) => {
              acc[index] = NCVMap[item.consumptionUnit] || null;
              return acc;
            }, {}),
          );
          setCarbonUnit(
            (data.cbamEminstNopfcList || []).reduce((acc, item, index) => {
              acc[index] = carbonMap[item.consumptionUnit] || null;
              return acc;
            }, {}),
          );
        }
      })
      .finally((fin) => {
        checkLoading(loading, false);
      });
  };

  const axios_F106030702Before = () => {
    checkLoading(loading);
    F106030702Before()
      .then((res) => {
        if (checkDpSuccess(res)) {
          setFormRule(processConstraints(res?.data?.RespBody?.constraints));
        }
      })
      .finally((fin) => {
        checkLoading(loading, false);
      });
  };

  const processConstraints = (constraints) => {
    return constraints.map((item) => {
      if (item.type === "collection") {
        return { [item?.field]: processConstraints(item?.subObject.value) };
      } else {
        return addFormValidator(item?.field, constraints);
      }
    });
  };

  const setValidForm = useCallback(
    (formId) => {
      if (formRule?.length > 0) {
        const tmp = formRule?.find((item) => {
          return Object.keys(item)[0] === formId;
        });
        return tmp ? tmp[formId] : "";
      }
    },
    [formRule],
  );

  const setValidNoPfcForm = useCallback(
    (formId) => {
      if (setValidForm("cbamEminstNopfcList")?.length > 0) {
        const tmp = setValidForm("cbamEminstNopfcList")?.find((item) => {
          return Object.keys(item)[0] === formId;
        });
        return tmp ? tmp[formId] : "";
      }
    },
    [setValidForm("cbamEminstNopfcList")],
  );

  const setValidPfcForm = useCallback(
    (formId) => {
      if (setValidForm("cbamEminstPfcList")?.length > 0) {
        const tmp = setValidForm("cbamEminstPfcList")?.find((item) => {
          return Object.keys(item)[0] === formId;
        });
        return tmp ? tmp[formId] : "";
      }
    },
    [setValidForm("cbamEminstPfcList")],
  );

  const setValidMeasForm = useCallback(
    (formId) => {
      if (setValidForm("cbamEminstMeasList")?.length > 0) {
        const tmp = setValidForm("cbamEminstMeasList")?.find((item) => {
          return Object.keys(item)[0] === formId;
        });
        return tmp ? tmp[formId] : "";
      }
    },
    [setValidForm("cbamEminstMeasList")],
  );

  const handleResetForm = (fieldArrayApi, defaultList, index) => {
    clearErrors();
    fieldArrayApi.remove(index);
    fieldArrayApi.append(defaultList);
  };

  const handleDelForm = (fieldArrayApi, defaultList, index, length) => {
    clearErrors();
    fieldArrayApi.remove(index);
    if (length <= 1) {
      fieldArrayApi.append(defaultList);
    }
  };

  const handleResetFormAllowEmpty = (fieldArrayApi, _defaultList, index) => {
    clearErrors();
    fieldArrayApi.remove(index);
  };

  const handleDelFormAllowEmpty = (fieldArrayApi, _defaultList, index) => {
    clearErrors();
    fieldArrayApi.remove(index);
  };

  const defaultNoPfcFields = {
    cbamEminstSeq: "",
  };

  const { fields: noPfcFields, append: addNoPfcForm, remove: delNoPfcForm, update: updateNoPfcForm } = useFieldArray({
    control,
    name: "cbamEminstNopfcList",
  });

  const noPfcPanelConfig = {
    header: () => (
      <>
        <span>{t("emissionSourceWithoutPFC")} </span>
        <Button
          disabled={editFlag}
          type="button"
          size="small"
          text
          severity="secondary"
          icon="pi pi-file-import"
          label={t("loadEquipmentProcess")}
          onClick={() => {
            setConfirmPopup(true);
          }}
        />
      </>
    ),
  };

  const noPfcDataTableConfig = {
    dataKey: "id",
    value: noPfcFields,
    className: `${editFlag || watch("cbamEminstNopfcList.length") >= 75 ? "last-row-no-border" : ""}`,
    footerEvent: () => addNoPfcForm(defaultNoPfcFields),
    footerDisabled: editFlag || watch("cbamEminstNopfcList.length") >= 75,
  };

  const noPfcColumnsConfig = [
    // 方法
    {
      field: "methodCode",
      fieldName: "methodCode",
      fieldListName: "cbamEminstNopfcList",
      header: t("method"),
      headerRequired: tabAction !== "detail",
      type: "dropdown",
      control: control,
      rules: setValidNoPfcForm("methodCode"),
      options: emissionTypeList,
      optionLabel: "subitemName",
      optionValue: "subitemNo",
      placeholder: t("plz_chs"),
      disabled: editFlag,
      onChange: (rowIndex, newValue) => {
        updateNoPfcForm(rowIndex, {
          ...getValues(`cbamEminstNopfcList.${rowIndex}`),
          emissionFactor: null,
          efUnit: newValue === "3" ? null : watch(`cbamEminstNopfcList.${rowIndex}.consumptionUnit`),
          carbonContent: null,
        });
        clearErrors();
        setValue(`cbamEminstNopfcList.${rowIndex}.emissionFactor`, null, { shouldValidate: true });
        setValue(
          `cbamEminstNopfcList.${rowIndex}.efUnit`,
          newValue === "3" ? null : watch(`cbamEminstNopfcList.${rowIndex}.consumptionUnit`),
          { shouldValidate: true },
        );
        setValue(`cbamEminstNopfcList.${rowIndex}.carbonContent`, null, { shouldValidate: true });
        setValue(`cbamEminstNopfcList.${rowIndex}.methodCode`, newValue, { shouldValidate: true });
        trigger();
      },
    },
    // 原然物料
    {
      field: "sourceName",
      fieldName: "sourceName",
      fieldListName: "cbamEminstNopfcList",
      header: t("rawMaterial"),
      headerRequired: tabAction !== "detail",
      type: "text",
      register: register,
      errors: errors,
      control: control,
      rules: setValidNoPfcForm("sourceName"),
      disabled: editFlag,
    },
    // 活動數據
    {
      field: "consumptionPerTon",
      fieldName: "consumptionPerTon",
      fieldListName: "cbamEminstNopfcList",
      header: t("activity_data"),
      headerRequired: tabAction !== "detail",
      type: "number",
      control: control,
      rules: setValidNoPfcForm("consumptionPerTon"),
      disabled: editFlag,
      value: (rowIndex) => watch(`cbamEminstNopfcList.${rowIndex}.consumptionPerTon`),
      onChange: (rowIndex, newValue) => {
        setValue(`cbamEminstNopfcList.${rowIndex}.consumptionPerTon`, newValue, { shouldValidate: true });
      },
    },
    // 單位
    {
      field: "consumptionUnit",
      fieldName: "consumptionUnit",
      fieldListName: "cbamEminstNopfcList",
      header: t("unit"),
      headerRequired: tabAction !== "detail",
      type: "dropdown",
      control: control,
      rules: setValidNoPfcForm("consumptionUnit"),
      options: adUnitList,
      optionLabel: "subitemName",
      optionValue: "subitemNo",
      placeholder: t("plz_chs"),
      onChange: (rowIndex, newValue) => {
        setNcvUnit((prev) => ({
          ...prev,
          [rowIndex]: NCVMap[newValue] || null,
        }));
        setCarbonUnit((prev) => ({
          ...prev,
          [rowIndex]: carbonMap[newValue] || null,
        }));
        setValue(`cbamEminstNopfcList.${rowIndex}.efUnit`, newValue, { shouldValidate: true });
      },
      disabled: editFlag,
    },
    // 淨熱值(NCV)
    {
      field: "netCalorificValue",
      fieldName: "netCalorificValue",
      fieldListName: "cbamEminstNopfcList",
      header: `${t("ncv")}(NCV)`,
      type: "number",
      control: control,
      rules: (rowIndex) => {
        return {
          ...setValidNoPfcForm("netCalorificValue"),
          required: {
            value: watch(`cbamEminstNopfcList.${rowIndex}.efUnit`) === "TJ",
            message: t("validation.required"),
          },
        };
      },
      displayText: (rowIndex) => {
        return ncvUnit[rowIndex] || "";
      },
      disabled: editFlag,
      value: (rowIndex) => watch(`cbamEminstNopfcList.${rowIndex}.netCalorificValue`),
      onChange: (rowIndex, newValue) => {
        setValue(`cbamEminstNopfcList.${rowIndex}.netCalorificValue`, newValue, { shouldValidate: true });
      },
    },
    // 排放係數(EF)
    {
      field: "emissionFactor",
      fieldName: "emissionFactor",
      fieldListName: "cbamEminstNopfcList",
      minWidth: "210px",
      header: `${t("ef")}(EF)`,
      type: "number",
      nonEditor: (value, rowIndex) => watch(`cbamEminstNopfcList.${rowIndex}.methodCode`) === "3",
      control: control,
      rules: (rowIndex) => {
        return {
          ...setValidNoPfcForm("emissionFactor"),
          required: {
            value: watch(`cbamEminstNopfcList.${rowIndex}.methodCode`) !== "3",
            message: t("validation.required"),
          },
        };
      },
      disabled: editFlag,
      value: (rowIndex) => watch(`cbamEminstNopfcList.${rowIndex}.emissionFactor`),
      onChange: (rowIndex, newValue) => {
        setValue(`cbamEminstNopfcList.${rowIndex}.emissionFactor`, newValue, { shouldValidate: true });
      },
    },
    // 排放係數單位
    {
      field: "efUnit",
      fieldName: "efUnit",
      fieldListName: "cbamEminstNopfcList",
      header: `${t("ef")}(EF)${t("unit")}`,
      type: "dropdown",
      nonEditor: (value, rowIndex) => watch(`cbamEminstNopfcList.${rowIndex}.methodCode`) === "3",
      control: control,
      rules: (rowIndex) => {
        return {
          ...setValidNoPfcForm("efUnit"),
          required: {
            value: watch(`cbamEminstNopfcList.${rowIndex}.methodCode`) !== "3",
            message: t("validation.required"),
          },
        };
      },
      options: efUnitList,
      optionLabel: "subitemName",
      optionValue: "subitemNo",
      placeholder: t("plz_chs"),
      disabled: editFlag,
      onChange: (rowIndex, newValue) => {
        clearErrors();
        trigger();
      },
    },
    // 含碳量
    {
      field: "carbonContent",
      fieldName: "carbonContent",
      fieldListName: "cbamEminstNopfcList",
      minWidth: "210px",
      header: t("carbonContent"),
      type: "number",
      control: control,
      nonEditor: (value, rowIndex) => watch(`cbamEminstNopfcList.${rowIndex}.methodCode`) !== "3",
      displayText: (rowIndex) => {
        return carbonUnit[rowIndex] || "";
      },
      rules: (rowIndex) => {
        return {
          ...setValidNoPfcForm("carbonContent"),
          required: {
            value: watch(`cbamEminstNopfcList.${rowIndex}.methodCode`) === "3",
            message: t("validation.required"),
          },
        };
      },
      disabled: editFlag,
      value: (rowIndex) => watch(`cbamEminstNopfcList.${rowIndex}.carbonContent`),
      onChange: (rowIndex, newValue) => {
        setValue(`cbamEminstNopfcList.${rowIndex}.carbonContent`, newValue);
      },
    },
    // 氧化因子(%)
    {
      field: "oxidationFactor",
      fieldName: "oxidationFactor",
      fieldListName: "cbamEminstNopfcList",
      header: `${t("oxidationFactor")}(%)`,
      type: "number",
      control: control,
      rules: setValidNoPfcForm("oxidationFactor"),
      disabled: editFlag,
      value: (rowIndex) => watch(`cbamEminstNopfcList.${rowIndex}.oxidationFactor`),
      onChange: (rowIndex, newValue) => {
        setValue(`cbamEminstNopfcList.${rowIndex}.oxidationFactor`, newValue);
      },
    },
    // 轉換因子(%)
    {
      field: "conversionFactor",
      fieldName: "conversionFactor",
      fieldListName: "cbamEminstNopfcList",
      header: `${t("conversionFactor")}(%)`,
      type: "number",
      control: control,
      rules: setValidNoPfcForm("conversionFactor"),
      disabled: editFlag,
      value: (rowIndex) => watch(`cbamEminstNopfcList.${rowIndex}.conversionFactor`),
      onChange: (rowIndex, newValue) => {
        setValue(`cbamEminstNopfcList.${rowIndex}.conversionFactor`, newValue);
      },
    },
    // 生質含量(%)
    {
      field: "biomassContent",
      fieldName: "biomassContent",
      fieldListName: "cbamEminstNopfcList",
      header: `${t("biomassContent")}(%)`,
      type: "number",
      control: control,
      rules: setValidNoPfcForm("biomassContent"),
      disabled: editFlag,
      value: (rowIndex) => watch(`cbamEminstNopfcList.${rowIndex}.biomassContent`),
      onChange: (rowIndex, newValue) => {
        setValue(`cbamEminstNopfcList.${rowIndex}.biomassContent`, newValue);
      },
    },
  ];

  const noPfcActionConfig = {
    fields: noPfcFields,
    fieldId: "id",
    handleDelForm: handleDelForm,
    handleResetForm: handleResetForm,
    fieldArrayApi: { remove: delNoPfcForm, append: addNoPfcForm },
    defaultList: defaultNoPfcFields,
    disabled: editFlag,
  };

  const defaultPfcFields = {
    cbamEminstPfcSeq: "",
  };

  const { fields: pfcFields, append: addPfcForm, remove: delPfcForm } = useFieldArray({
    control,
    name: "cbamEminstPfcList",
  });

  const pfcPanelConfig = {
    header: t("emissionSourcePFC"),
    // 預設收合：減少初次進入畫面時的資訊量
    collapsed: true,
  };

  const pfcDataTableConfig = {
    dataKey: "id",
    value: pfcFields,
    className: `${editFlag || watch("cbamEminstPfcList.length") >= 10 ? "last-row-no-border" : ""}`,
    footerEvent: () => addPfcForm(defaultPfcFields),
    footerDisabled: editFlag || watch("cbamEminstPfcList.length") >= 10,
  };

  const pfcColumnsConfig = [
    // 方法
    {
      field: "pfcMethodCode",
      fieldName: "pfcMethodCode",
      fieldListName: "cbamEminstPfcList",
      header: t("emissionCategory"),
      headerRequired: tabAction !== "detail",
      type: "dropdown",
      control: control,
      rules: setValidPfcForm("pfcMethodCode"),
      options: methodList,
      optionLabel: "subitemName",
      optionValue: "subitemNo",
      placeholder: t("plz_chs"),
      disabled: editFlag,
    },
    // 原然物料
    {
      field: "technologyType",
      fieldName: "technologyType",
      fieldListName: "cbamEminstPfcList",
      header: t("rawMaterial"),
      headerRequired: tabAction !== "detail",
      type: "text",
      register: register,
      errors: errors,
      control: control,
      rules: setValidPfcForm("technologyType"),
      disabled: editFlag,
    },
    // 活動數據
    {
      field: "consumptionPerTon",
      fieldName: "consumptionPerTon",
      fieldListName: "cbamEminstPfcList",
      header: t("activity_data"),
      headerRequired: tabAction !== "detail",
      type: "number",
      control: control,
      rules: setValidNoPfcForm("consumptionPerTon"),
      disabled: editFlag,
      value: (rowIndex) => watch(`cbamEminstPfcList.${rowIndex}.consumptionPerTon`),
      onChange: (rowIndex, newValue) => {
        setValue(`cbamEminstPfcList.${rowIndex}.consumptionPerTon`, newValue);
      },
    },
    // 單位
    {
      field: "consumptionUnit",
      fieldName: "consumptionUnit",
      fieldListName: "cbamEminstPfcList",
      header: t("unit"),
      headerRequired: tabAction !== "detail",
      type: "dropdown",
      control: control,
      rules: setValidPfcForm("consumptionUnit"),
      options: adUnitList,
      optionLabel: "subitemName",
      optionValue: "subitemNo",
      placeholder: t("plz_chs"),
      disabled: editFlag,
    },
  ];

  const pfcActionConfig = {
    fields: pfcFields,
    fieldId: "id",
    handleDelForm: handleDelFormAllowEmpty,
    handleResetForm: handleResetFormAllowEmpty,
    fieldArrayApi: { remove: delPfcForm, append: addPfcForm },
    defaultList: defaultPfcFields,
    disabled: editFlag,
  };

  const defaultMeasFields = {
    cbamEminstMeasSeq: "",
  };

  const { fields: MeasFields, append: addMeasForm, remove: delMeasForm } = useFieldArray({
    control,
    name: "cbamEminstMeasList",
  });

  const measPanelConfig = {
    header: t("measuredProcessedEmissionSource"),
    // 預設收合：需要時再展開編輯
    collapsed: true,
  };

  const measDataTableConfig = {
    dataKey: "id",
    value: MeasFields,
    className: `${editFlag || watch("cbamEminstMeasList.length") >= 10 ? "last-row-no-border" : ""}`,
    footerEvent: () => addMeasForm(defaultMeasFields),
    footerDisabled: editFlag || watch("cbamEminstMeasList.length") >= 10,
  };

  const measColumnsConfig = [
    // 名稱
    {
      field: "measName",
      fieldName: "measName",
      fieldListName: "cbamEminstMeasList",
      header: t("productName"),
      headerRequired: tabAction !== "detail",
      type: "text",
      register: register,
      errors: errors,
      control: control,
      rules: setValidMeasForm("measName"),
      disabled: editFlag,
    },
    // 氣體類型
    {
      field: "ghgType",
      fieldName: "ghgType",
      fieldListName: "cbamEminstMeasList",
      header: t("gasType"),
      headerRequired: tabAction !== "detail",
      type: "dropdown",
      control: control,
      rules: setValidMeasForm("ghgType"),
      options: gasTypeList,
      optionLabel: "subitemName",
      optionValue: "subitemNo",
      placeholder: t("plz_chs"),
      disabled: editFlag,
    },
    // 生質能源比例
    {
      field: "biomassFraction",
      fieldName: "biomassFraction",
      fieldListName: "cbamEminstMeasList",
      header: `${t("biomassEnergyRatio")}(%)`,
      type: "number",
      control: control,
      rules: setValidMeasForm("biomassFraction"),
      disabled: editFlag,
      value: (rowIndex) => watch(`cbamEminstMeasList.${rowIndex}.biomassFraction`),
      onChange: (rowIndex, newValue) => {
        setValue(`cbamEminstMeasList.${rowIndex}.biomassFraction`, newValue);
      },
    },
    // 每小時氣體濃度平均值
    {
      field: "avgHourlyGhgConc",
      fieldName: "avgHourlyGhgConc",
      fieldListName: "cbamEminstMeasList",
      header: `${t("avgGasConcentrationPerHour")}(g/Nm3)`,
      type: "number",
      control: control,
      rules: setValidMeasForm("avgHourlyGhgConc"),
      disabled: editFlag,
      value: (rowIndex) => watch(`cbamEminstMeasList.${rowIndex}.avgHourlyGhgConc`),
      onChange: (rowIndex, newValue) => {
        setValue(`cbamEminstMeasList.${rowIndex}.avgHourlyGhgConc`, newValue);
      },
    },
    // 總運行時數
    {
      field: "operatingHours",
      fieldName: "operatingHours",
      fieldListName: "cbamEminstMeasList",
      header: `${t("totalOperatingHours")}(h/period)`,
      type: "number",
      control: control,
      rules: setValidMeasForm("operatingHours"),
      disabled: editFlag,
      value: (rowIndex) => watch(`cbamEminstMeasList.${rowIndex}.operatingHours`),
      onChange: (rowIndex, newValue) => {
        setValue(`cbamEminstMeasList.${rowIndex}.operatingHours`, newValue);
      },
    },
    // 平均排煙流量
    {
      field: "avgFlueGasFlow",
      fieldName: "avgFlueGasFlow",
      fieldListName: "cbamEminstMeasList",
      header: `${t("avgFlueGasFlow")}(1000Nm3/h)`,
      type: "number",
      control: control,
      rules: setValidMeasForm("avgFlueGasFlow"),
      disabled: editFlag,
      value: (rowIndex) => watch(`cbamEminstMeasList.${rowIndex}.avgFlueGasFlow`),
      onChange: (rowIndex, newValue) => {
        setValue(`cbamEminstMeasList.${rowIndex}.avgFlueGasFlow`, newValue);
      },
    },
    // 化石能源含量
    {
      field: "energyContentFossilTj",
      fieldName: "energyContentFossilTj",
      fieldListName: "cbamEminstMeasList",
      header: `${t("fossilEnergyContent")}(TJ)`,
      type: "number",
      control: control,
      rules: setValidMeasForm("energyContentFossilTj"),
      disabled: editFlag,
      value: (rowIndex) => watch(`cbamEminstMeasList.${rowIndex}.energyContentFossilTj`),
      onChange: (rowIndex, newValue) => {
        setValue(`cbamEminstMeasList.${rowIndex}.energyContentFossilTj`, newValue);
      },
    },
    // 生質能源含量
    {
      field: "energyContentBioTj",
      fieldName: "energyContentBioTj",
      fieldListName: "cbamEminstMeasList",
      header: `${t("biomassEnergyContent")}(TJ)`,
      type: "number",
      control: control,
      rules: setValidMeasForm("energyContentBioTj"),
      disabled: editFlag,
      value: (rowIndex) => watch(`cbamEminstMeasList.${rowIndex}.energyContentBioTj`),
      onChange: (rowIndex, newValue) => {
        setValue(`cbamEminstMeasList.${rowIndex}.energyContentBioTj`, newValue);
      },
    },
  ];

  const measActionConfig = {
    fields: pfcFields,
    fieldId: "id",
    handleDelForm: handleDelFormAllowEmpty,
    handleResetForm: handleResetFormAllowEmpty,
    fieldArrayApi: { remove: delMeasForm, append: addMeasForm },
    defaultList: defaultMeasFields,
    disabled: editFlag,
  };

  const encoder = (dataList, code) => {
    const findItemIndex = (map, code) => {
      return map.get(code) ?? false;
    };
    if (code === null || code === undefined || code === "") return "";
    if (dataList === null || dataList === undefined || dataList === "" || dataList.length <= 0) return "";
    return Base64Encoder(BcryptEncoder(code ?? "")) + "," + findItemIndex(dataList, code ?? "");
  };

  const onSubmit = async (data) => {
    checkLoading(loading);

    await new Promise((resolve) => setTimeout(resolve, 0));

    const emissionTypeMap = new Map(emissionTypeList.map((v, i) => [v.subitemNo, v.index]));
    const adUnitMap = new Map(adUnitList.map((v, i) => [v.subitemNo, v.index]));
    const efUnitMap = new Map(efUnitList.map((v, i) => [v.subitemNo, v.index]));
    const methodMap = new Map(methodList.map((v, i) => [v.subitemNo, v.index]));
    const gasTypeMap = new Map(gasTypeList.map((v, i) => [v.subitemNo, v.index]));

    if (data?.cbamEminstNopfcList) {
      await Promise.all(
        data.cbamEminstNopfcList.map(async (item) => {
          item.methodCode = encoder(emissionTypeMap, item.methodCode);
          item.consumptionUnit = encoder(adUnitMap, item.consumptionUnit);
          if (item.efUnit != null && item.efUnit !== "") {
            item.efUnit = encoder(efUnitMap, item.efUnit);
          }
        }),
      );
    }

    if (data?.cbamEminstPfcList) {
      await Promise.all(
        data.cbamEminstPfcList.map(async (item) => {
          item.pfcMethodCode = encoder(methodMap, item.pfcMethodCode ?? "");
        }),
      );
    }

    if (data?.cbamEminstMeasList) {
      await Promise.all(
        data.cbamEminstMeasList.map(async (item) => {
          item.ghgType = encoder(gasTypeMap, item.ghgType);
        }),
      );
    }

    let reqBody = {
      cbamMasterSeq: currentCbamMasterSeq,
      cbamEminstNopfcList: data.cbamEminstNopfcList,
      cbamEminstPfcList: data.cbamEminstPfcList,
      cbamEminstMeasList: data.cbamEminstMeasList,
    };
    updateCbamEmissionSource(reqBody)
      .then((res) => {
        if (checkDpSuccess(res)) {
          handleSuccessMessage(dispatch, t("button.update") + " " + t("message.success"));
          setActiveIndex(3);
        }
      })
      .finally((fin) => {
        checkLoading(loading, false);
      });
  };

  return (
    <form className="grid" onSubmit={handleSubmit(onSubmit)}>
      {/* 不含PFC(全氟碳化物)之排放源 */}
      <div className="col-12">
        <PanelDataTable
          panelConfig={noPfcPanelConfig}
          dataTableConfig={noPfcDataTableConfig}
          columnsConfig={noPfcColumnsConfig}
          actionConfig={noPfcActionConfig}
        />
      </div>
      {/* PFC(全氟碳化物)之排放源 */}
      <div className="col-12">
        <PanelDataTable
          panelConfig={pfcPanelConfig}
          dataTableConfig={pfcDataTableConfig}
          columnsConfig={pfcColumnsConfig}
          actionConfig={pfcActionConfig}
        />
      </div>
      {/* 測量處理的排放源 */}
      <div className="col-12">
        <PanelDataTable
          panelConfig={measPanelConfig}
          dataTableConfig={measDataTableConfig}
          columnsConfig={measColumnsConfig}
          actionConfig={measActionConfig}
        />
      </div>

      <div className="col-12 gap-2 flex-all-center">
        {tabAction === "detail" && (
          <Button
            disabled={!isEditable}
            type="button"
            label={t("button.edit")}
            size="small"
            onClick={() => setTabAction("create")}
          />
        )}

        {tabAction === "create" && (
          <>
            <Button
              disabled={!(isValid && isDirty)}
              type="submit"
              label={action === "create" ? t("button.create") : t("button.update")}
              size="small"
            />
            {action !== "create" && (
              <Button
                outlined
                size="small"
                label={t("button.cancel")}
                onClick={(e) => {
                  e.preventDefault();
                  clearErrors();
                  setTabAction("detail");
                  reset(oriData);
                }}
              />
            )}
          </>
        )}
      </div>

      <PrimeDialog
        visible={confirmPopup}
        onClose={() => {
          setConfirmPopup(false);
        }}
        onConfirm={() => {
          axios_defaultEmissionSourceLoad({
            companySeq: currentCompany?.companySeq,
            cbamMasterSeq: currentCbamMasterSeq,
          });
          setConfirmPopup(false);
        }}
        title={t("hint")}
        size="sm"
        bodyTemplate={t("loadEquipmentProcessTip")}
      />
    </form>
  );
};
export default EmissionSourceInfo;
